<template>
  <Card class="p-4 rounded-2xl" :style="cardStyle">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col">
        <span class="special-giftcard-buy-box__title">
          {{ $t('product.show.checkout.specialGiftcard.title') }}
        </span>
        <span class="special-giftcard-buy-box__subtitle">
          {{ $t('product.show.checkout.specialGiftcard.subtitle') }}
        </span>
      </div>

      <EventBuyBoxPrice
        :selected-ticket-quantity="1"
        :kft-event="eventData"
        :loading-pricings="loadingPricings"
        :type="type"
        is-giftcard
      >
        <template #main-price="{ totalPriceToDisplay }">
          <div class="special-giftcard-buy-box__price">
            <!-- loader -->
            <div
              class="h-[28px] rounded-md"
              :class="{
                'w-20 bg-gray-200 animate-pulse':
                  loadingPricings || !totalPriceToDisplay,
              }"
            >
              <span v-if="!loadingPricings && totalPriceToDisplay">
                {{ totalPriceToDisplay }}
              </span>
            </div>
          </div>
        </template>
      </EventBuyBoxPrice>
    </div>

    <GiftcardDeliveryOptions
      v-model="selectedDeliveryOption"
      class="mt-4"
      :label="$t('product.show.checkout.specialGiftcard.deliveryOptionsLabel')"
    />

    <Button
      class="mt-4 ease w-full py-3 duration-300"
      :style="ctaStyle"
      :disabled="!giftcardPricings"
      data-test-id="special-giftcard-buy-button"
      @click="gift"
    >
      {{ $t('product.show.checkout.bookGiftcard') }}
    </Button>
  </Card>
</template>

<script lang="ts" setup>
import GiftcardDeliveryOptions from '~/components/giftcard/GiftcardDeliveryOptions.vue'
import type { EventDateType, EventDescription } from '~/composables/types/event'
import type { CartItem, GiftcardAddon } from '~/composables/types/cart'
import { useGiftcardAddToCart } from '~/composables/useGiftcardAddToCart'

const {
  type = 'PUBLIC',
  enableEmbedded = false,
  textColor = undefined,
  backgroundColor = undefined,
  cardBackgroundColor = undefined,
} = defineProps<{
  type?: EventDateType
  enableEmbedded?: boolean
  textColor?: string
  backgroundColor?: string
  cardBackgroundColor?: string
}>()

const emit = defineEmits<{
  (e: 'add-item-to-cart', cartItem: CartItem): void
}>()

const { t: $t } = useI18n()
const eventData = inject('eventData') as EventDescription
const { addGiftcardToCart } = useGiftcardAddToCart()

const selectedDeliveryOption = ref<GiftcardAddon>('PDF')

const cardStyle = computed(() => ({
  ...(cardBackgroundColor && { backgroundColor: cardBackgroundColor }),
}))

const ctaStyle = computed(() => ({
  ...(textColor && { color: `#${textColor}` }),
  ...(backgroundColor && { backgroundColor: `#${backgroundColor}` }),
}))

const { getInitialCategoryQuantities } = useTicketQuantityCalculations()
const ticketCategoriesArray = computed<PricingCategory[]>(
  () => eventData?.pricing?.data?.pricingCategories?.data || []
)
const selectedCategoryQuantities = ref<Record<string, number>>(
  getInitialCategoryQuantities(ticketCategoriesArray.value)
)

const {
  fetchPricings,
  pricings: pricingsData,
  loading: loadingPricings,
} = useEventPricings(ref(eventData) as Ref<EventDescription | undefined>, {
  dateId: ref(undefined),
  quantity: ref(1),
  isPrivate: ref(false),
  type: ref('GIFT_CARD'),
  categoryQuantities: selectedCategoryQuantities,
})

// EventBuyBoxPrice reads the pricings from this injection key.
provide('pricingsData', pricingsData)

const giftcardPricings = computed(() => Object.values(pricingsData.value)[0])

const gift = async () => {
  if (!giftcardPricings.value) return

  const cartItem = await addGiftcardToCart({
    event: eventData,
    pricings: giftcardPricings.value,
    giftcardType: 'SPECIAL',
    giftcardAddon: selectedDeliveryOption.value,
    clearCartFirst: enableEmbedded,
  })

  if (cartItem) {
    emit('add-item-to-cart', cartItem)
  }
}

onMounted(async () => {
  await fetchPricings()
})
</script>

<style lang="postcss" scoped>
.special-giftcard-buy-box {
  &__title {
    @apply font-lexend text-body-xl font-semibold text-gray-900;
  }

  &__subtitle {
    @apply text-caption-large font-normal text-gray-500;
  }

  &__price {
    /* Matches &__title */
    @apply font-lexend text-body-xl font-semibold text-gray-900;
    @apply text-nowrap flex gap-2 items-center min-h-[28px];
  }
}
</style>
